/**
 * Página interna pra validação visual dos helpers de export
 * (`shared/reports/*`). Dataset fake fixo — dispara PDF/XLSX no clique.
 *
 * Rota: `/dev/report-preview` (registrada em `routes.tsx`).
 */
import { Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../../shared/auth/useAuth';
import { downloadAsPdf } from '../../shared/reports/downloadAsPdf';
import { downloadAsXlsx } from '../../shared/reports/downloadAsXlsx';
import {
  buildFilename,
  formatDate,
  formatMoney,
  formatPeriodo,
} from '../../shared/reports/formatters';

interface LinhaFake {
  data: string;
  diaSemana: string;
  horasLancadas: string;
  falta: boolean;
  observacao: string;
  valor: number;
}

const LINHAS: LinhaFake[] = [
  {
    data: '2026-06-01',
    diaSemana: 'SEG',
    horasLancadas: '08:00',
    falta: false,
    observacao: 'Sprint planning — projeto Açúcar',
    valor: 720,
  },
  {
    data: '2026-06-02',
    diaSemana: 'TER',
    horasLancadas: '08:30',
    falta: false,
    observacao: 'Refatoração ETL',
    valor: 765,
  },
  {
    data: '2026-06-03',
    diaSemana: 'QUA',
    horasLancadas: '00:00',
    falta: true,
    observacao: 'Atestado médico',
    valor: 0,
  },
  {
    data: '2026-06-04',
    diaSemana: 'QUI',
    horasLancadas: '09:15',
    falta: false,
    observacao: 'Deploy + acompanhamento',
    valor: 833.75,
  },
  {
    data: '2026-06-05',
    diaSemana: 'SEX',
    horasLancadas: '08:00',
    falta: false,
    observacao: 'Code review + 1:1',
    valor: 720,
  },
];

const TOTAL_HORAS = '33:45';
const TOTAL_VALOR = LINHAS.reduce((acc, l) => acc + l.valor, 0);

export function ReportPreview(): JSX.Element {
  const { user } = useAuth();
  const usuario = user?.nome ?? 'desenvolvimento';

  function gerarPdf(): void {
    const meta = {
      title: 'Espelho de Ponto (preview)',
      subtitle: `ERICK QUEIROZ — ${formatPeriodo(6, 2026)}`,
      filters: ['Cliente: LOUIS DREYFUS', 'Status: SOL+'],
      usuario,
    };
    downloadAsPdf(
      buildFilename('preview-espelho', 'erick', 'junho-2026'),
      meta,
      ({ doc, contentStartY }) => {
        autoTable(doc, {
          startY: contentStartY,
          head: [['Data', 'Dia', 'Horas', 'Falta', 'Observação', 'Valor']],
          body: LINHAS.map((l) => [
            formatDate(l.data),
            l.diaSemana,
            l.horasLancadas,
            l.falta ? 'Sim' : '',
            l.observacao,
            formatMoney(l.valor),
          ]),
          foot: [
            [
              { content: 'Totais', colSpan: 2, styles: { halign: 'right', fontStyle: 'bold' } },
              TOTAL_HORAS,
              '',
              '',
              formatMoney(TOTAL_VALOR),
            ],
          ],
          styles: { fontSize: 9 },
          headStyles: { fillColor: [30, 60, 100] },
          footStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: 'bold' },
        });
      },
    );
  }

  function gerarXlsx(): void {
    downloadAsXlsx(buildFilename('preview-espelho', 'erick', 'junho-2026'), [
      {
        name: 'Espelho',
        rows: LINHAS.map((l) => ({
          Data: l.data,
          Dia: l.diaSemana,
          Horas: l.horasLancadas,
          Falta: l.falta ? 'Sim' : '',
          Observação: l.observacao,
          Valor: l.valor,
        })),
        columns: [
          { key: 'Data', header: 'Data', format: 'date', width: 12 },
          { key: 'Dia', header: 'Dia', width: 6 },
          { key: 'Horas', header: 'Horas', width: 8 },
          { key: 'Falta', header: 'Falta', width: 8 },
          { key: 'Observação', header: 'Observação', width: 40 },
          { key: 'Valor', header: 'Valor', format: 'money', width: 14 },
        ],
      },
      {
        name: 'Resumo',
        rows: [
          { Indicador: 'Total horas', Valor: TOTAL_HORAS },
          { Indicador: 'Total faturado', Valor: TOTAL_VALOR },
        ],
        columns: [
          { key: 'Indicador', header: 'Indicador', width: 24 },
          { key: 'Valor', header: 'Valor', format: 'money', width: 14 },
        ],
      },
    ]);
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Report preview (dev)
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Dataset fake pra validar header/footer/paginação dos helpers em
        <code> shared/reports/</code>. Cobre acentos PT-BR (ç, ã, õ), moeda e data BR, e exporta
        tanto PDF quanto XLSX a partir das mesmas linhas.
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Button variant="contained" startIcon={<PictureAsPdfIcon />} onClick={gerarPdf}>
          Exportar PDF
        </Button>
        <Button variant="outlined" startIcon={<TableViewIcon />} onClick={gerarXlsx}>
          Exportar XLSX
        </Button>
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Linhas (5)
          </Typography>
          <Stack divider={<div style={{ borderBottom: '1px solid #eee' }} />}>
            {LINHAS.map((l) => (
              <Stack key={l.data} direction="row" spacing={2} sx={{ py: 0.5 }}>
                <Typography sx={{ width: 90, fontFamily: 'monospace' }}>{l.data}</Typography>
                <Typography sx={{ width: 40 }}>{l.diaSemana}</Typography>
                <Typography sx={{ width: 60, fontFamily: 'monospace' }}>
                  {l.horasLancadas}
                </Typography>
                <Typography sx={{ flex: 1 }} color={l.falta ? 'error' : undefined}>
                  {l.falta ? '[FALTA] ' : ''}
                  {l.observacao}
                </Typography>
                <Typography sx={{ width: 100, fontFamily: 'monospace', textAlign: 'right' }}>
                  {formatMoney(l.valor)}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mt: 2, pt: 1, borderTop: 2 }}>
            <Typography sx={{ flex: 1, fontWeight: 600 }}>Totais</Typography>
            <Typography sx={{ width: 60, fontFamily: 'monospace', fontWeight: 600 }}>
              {TOTAL_HORAS}
            </Typography>
            <Typography
              sx={{ width: 100, fontFamily: 'monospace', fontWeight: 600, textAlign: 'right' }}
            >
              {formatMoney(TOTAL_VALOR)}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
